import { createContext, useContext, useEffect, useMemo, useState } from "react"

/**
 * The FocusStackContext provides a way to manage multiple focus traps in a stack-like manner.
 * This is useful for scenarios where multiple modals or popups may be open at the same time,
 * ensuring that focus is correctly trapped within the topmost element.
 */
export const FocusStackContext = createContext<{
  insertFocusTrap: (trap: (e: KeyboardEvent) => void) => void
  removeFocusTrap: (trap: (e: KeyboardEvent) => void) => void
} | null>(null)

/**
 * Refocuses the first focusable child element within the given parent element.
 */
export const refocusChildren = (parent: HTMLDivElement, direction: "forward" | "backward") => {
  if (!parent) {
    return
  }

  const focusables = parent.querySelectorAll('button, [href], input, select, textarea, li, a, [tabindex="0"]')
  if (focusables && focusables.length > 0) {
    let firstFocusable: HTMLElement | null = null
    let foundFocused = false
    let didFocus = false

    for (
      let i = direction === "forward" ? 0 : focusables.length - 1;
      direction === "forward" ? i < focusables.length : i >= 0;
      direction === "forward" ? i++ : i--
    ) {
      const el = focusables[i] as HTMLElement
      const isFocusable =
        !el.hasAttribute("disabled") && el.getAttribute("aria-hidden") !== "true" && el.style.display !== "none"

      if (!firstFocusable && isFocusable) {
        firstFocusable = el
      }

      if (el === document.activeElement) {
        foundFocused = true
      } else {
        if (foundFocused && isFocusable) {
          el.focus()
          didFocus = true
          break
        }
      }
    }

    if (!didFocus && firstFocusable) {
      firstFocusable.focus()
    }
  }
}

/**
 * Creates a focus trap function for the given element. Also stops all propagation of the focus element to any other handlers that may be registered.
 */
export const createFocusTrap = (el: HTMLDivElement | null) => {
  // @ts-ignore Try to blur the currently focused element if possible
  document.activeElement?.blur?.()
  // @ts-ignore Then try to focus the provided element if possible
  el.focus?.()

  return (e: KeyboardEvent) => {
    if (!el) {
      return false
    }
    if (e.key !== "Tab") {
      return false
    }

    e.stopImmediatePropagation()
    e.preventDefault()

    const direction = e.shiftKey ? "backward" : "forward"
    refocusChildren(el, direction)
  }
}

/**
 * Hook to manager focus traps in a stack-like manner.
 * If no stack has been provided via context, a new stack is created.
 * Once inside the stacked context components can register and unregister focus traps. The latest registered trap is the active one.
 * This ensures there is only ever one global focus handler active on the document, which makes it much easier to handle the logic of multiple modals/popups with focus traps.
 */
export const useFocusStack = () => {
  const focusContext = useContext(FocusStackContext)
  const [focusStack, setFocusStack] = useState<Array<(e: KeyboardEvent) => void>>([])

  useEffect(() => {
    if (focusContext) {
      return
    }

    const focusTrap = (e: KeyboardEvent) => {
      if (focusStack.length === 0) {
        return
      }
      const topTrap = focusStack[0]
      topTrap(e)
    }

    document.addEventListener("keydown", focusTrap, {
      capture: true,
    })

    return () => {
      document.removeEventListener("keydown", focusTrap, {
        capture: true,
      })
    }
  }, [focusContext, focusStack])

  const newFocusContext = useMemo(
    () => ({
      insertFocusTrap: (trap: (e: KeyboardEvent) => void) => {
        setFocusStack((prev) => {
          const newStack = [trap, ...prev]
          return newStack
        })
      },
      removeFocusTrap: (trap: (e: KeyboardEvent) => void) => {
        setFocusStack((prev) => {
          const newStack = prev.filter((t) => t !== trap)
          return newStack
        })
      },
    }),
    [],
  )

  if (focusContext) {
    return focusContext
  }

  return newFocusContext
}
